/* Role dashboard widgets for the replacement workflow.
   Presentation-only — data comes from existing APIs, reused across dashboards. */
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/lib/api";
import { QueueCard, QueueRow, StatusBadge, EmptyState } from "@/components/common";
import { SourceBadge, LeaveNoticesPanel } from "@/pages/reschedule/ReschedulePages";
import { Hourglass, Timer } from "lucide-react";

/* Teacher dashboard: replacement tasks created from approved leave +
   lessons with replacement minutes still owed. */
export function ReplacementTasksCard() {
  const { data: requests } = useList("/reschedule-requests");
  if (!requests) return null;
  const tasks = requests.filter((r) => r.status === "awaiting_teacher_proposal");
  const partial = requests.filter((r) => r.replacement_status === "partially_replaced"
    && !["cancelled", "rejected"].includes(r.status));
  if (tasks.length === 0 && partial.length === 0) return null;
  return (
    <QueueCard title="Replacement tasks" icon={Hourglass} count={tasks.length + partial.length}
      tone="warning" testId="replacement-tasks-card">
      {tasks.map((r) => (
        <QueueRow key={r.id} to={`/teacher/reschedule/${r.id}`}
          primary={<>{r.student_name} · {r.course_name}</>}
          secondary={`Missed ${r.original_date} ${r.original_start_time} — propose a replacement time`}
          right={<SourceBadge source={r.source_type} />}
          testId={`task-${r.id}`} />
      ))}
      {partial.map((r) => (
        <QueueRow key={r.id} to={`/teacher/reschedule/${r.id}`}
          primary={<>{r.student_name} · {r.course_name}</>}
          secondary={`${r.minutes_remaining} min still owed — add a 15-min extension to a future lesson`}
          right={<Timer className="h-4 w-4 text-[#B45309]" />}
          testId={`partial-${r.id}`} />
      ))}
    </QueueCard>
  );
}

/* Parent dashboard: acknowledge notices + requests needing a response +
   replacement minutes still owed for their children. */
export function ParentActionCenter() {
  const { user } = useAuth();
  const { data: requests } = useList("/reschedule-requests");
  const needResponse = (requests || []).filter((r) => r.status === "pending_parent_response");
  const partial = (requests || []).filter((r) => r.replacement_status === "partially_replaced"
    && !["cancelled", "rejected"].includes(r.status));
  return (
    <div className="mt-6 space-y-4" data-testid="parent-action-center">
      <LeaveNoticesPanel role={user?.role === "student" ? "student" : "parent"} />
      {(needResponse.length > 0 || partial.length > 0) && (
        <QueueCard title="Replacement classes — action needed" icon={Hourglass}
          count={needResponse.length + partial.length} tone="warning" testId="parent-replacement-card">
          {needResponse.map((r) => (
            <QueueRow key={r.id} to={`/parent/reschedule/${r.id}`}
              primary={<>{r.student_name} · {r.course_name}</>}
              secondary="The teacher proposed a replacement time — accept or counter-propose"
              right={<StatusBadge status={r.status} />}
              testId={`parent-respond-${r.id}`} />
          ))}
          {partial.map((r) => (
            <QueueRow key={r.id} to={`/parent/reschedule/${r.id}`}
              primary={<>{r.student_name} · {r.course_name}</>}
              secondary={`${r.minutes_completed}/${r.minutes_owed} replacement minutes done · ${r.minutes_remaining} min remaining (never expires)`}
              right={<StatusBadge status="partially_replaced" />}
              testId={`parent-partial-${r.id}`} />
          ))}
        </QueueCard>
      )}
    </div>
  );
}
